import { initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile as updateFirebaseProfile,
  User as FirebaseUser,
} from 'firebase/auth';
import { getFunctions, Functions, httpsCallable } from 'firebase/functions';

import {
  User,
  LoginCredentials,
  SignupCredentials,
  AuthResult,
  PasswordResetRequest,
  UserProfileUpdate,
  AuthProvider,
} from '@orbit/shared';
import { mapFirebaseError } from '@orbit/shared';

export class FirebaseAuthService {
  private app: FirebaseApp;
  private auth: Auth;
  private functions: Functions;

  constructor(
    config: {
      apiKey: string;
      authDomain: string;
      projectId: string;
      storageBucket: string;
      messagingSenderId: string;
      appId: string;
    },
    existingApp?: FirebaseApp,
  ) {
    this.app = existingApp || initializeApp(config);
    this.auth = getAuth(this.app);
    this.functions = getFunctions(this.app, 'us-central1');

    // Note: Emulator connection is now handled by the centralized Firebase config
    // This constructor no longer handles emulator connections
  }

  // Convert Firebase user to our User type
  private mapFirebaseUser(firebaseUser: FirebaseUser): User {
    return {
      id: firebaseUser.uid,
      email: firebaseUser.email || '',
      displayName: firebaseUser.displayName || undefined,
      photoURL: firebaseUser.photoURL || undefined,
      emailVerified: firebaseUser.emailVerified,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
  }

  // Get or create user profile from Firestore
  private async getUserProfile(): Promise<User> {
    try {
      const getUserProfile = httpsCallable(this.functions, 'getUserProfile');
      const result = await getUserProfile();
      const user = (result.data as { user: User }).user;
      return user;
    } catch {
      // If profile doesn't exist, create one
      const createUserProfile = httpsCallable(this.functions, 'createUserProfile');
      const result = await createUserProfile();
      return (result.data as { user: User }).user;
    }
  }

  // Email/Password Authentication
  async login(credentials: LoginCredentials): Promise<AuthResult> {
    try {
      await signInWithEmailAndPassword(this.auth, credentials.email, credentials.password);

      const userProfile = await this.getUserProfile();

      return {
        success: true,
        user: userProfile,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: mapFirebaseError(error),
      };
    }
  }

  async signup(credentials: SignupCredentials): Promise<AuthResult> {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        this.auth,
        credentials.email,
        credentials.password,
      );

      // Update display name if provided
      if (credentials.displayName) {
        await updateFirebaseProfile(userCredential.user, {
          displayName: credentials.displayName,
        });
      }

      // Create user profile in Firestore
      const createUserProfile = httpsCallable(this.functions, 'createUserProfile');
      await createUserProfile({
        displayName: credentials.displayName,
        photoURL: userCredential.user.photoURL,
      });

      const userProfile = await this.getUserProfile();

      return {
        success: true,
        user: userProfile,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: mapFirebaseError(error),
      };
    }
  }

  // Provider Authentication
  async loginWithProvider(provider: AuthProvider): Promise<AuthResult> {
    try {
      let authProvider;

      switch (provider.id) {
        case 'google':
          authProvider = new GoogleAuthProvider();
          break;
        default:
          throw new Error(`Unsupported provider: ${provider.id}`);
      }

      await signInWithPopup(this.auth, authProvider);
      const userProfile = await this.getUserProfile();

      return {
        success: true,
        user: userProfile,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: mapFirebaseError(error),
      };
    }
  }

  // Logout
  async logout(): Promise<AuthResult> {
    try {
      await signOut(this.auth);
      return { success: true };
    } catch (error: unknown) {
      return {
        success: false,
        error: mapFirebaseError(error),
      };
    }
  }

  // Password Reset
  async resetPassword(request: PasswordResetRequest): Promise<AuthResult> {
    try {
      await sendPasswordResetEmail(this.auth, request.email);
      return { success: true };
    } catch (error: unknown) {
      return {
        success: false,
        error: mapFirebaseError(error),
      };
    }
  }

  // Profile Updates
  async updateProfile(updates: UserProfileUpdate): Promise<AuthResult> {
    try {
      const currentUser = this.auth.currentUser;
      if (!currentUser) {
        throw new Error('No authenticated user');
      }

      await updateFirebaseProfile(currentUser, updates);
      const userProfile = await this.getUserProfile();

      return {
        success: true,
        user: userProfile,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: mapFirebaseError(error),
      };
    }
  }

  // Save user bio and complete onboarding
  async saveUserBioAndCompleteOnboarding(userBio: any): Promise<AuthResult> {
    try {
      const saveUserBioAndCompleteOnboarding = httpsCallable(
        this.functions,
        'saveUserBioAndCompleteOnboarding',
      );
      await saveUserBioAndCompleteOnboarding({ userBio });

      return {
        success: true,
      };
    } catch (error: unknown) {
      return {
        success: false,
        error: mapFirebaseError(error),
      };
    }
  }

  // Auth State Observer
  onAuthStateChanged(callback: (user: User | null) => void): () => void {
    return onAuthStateChanged(this.auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userProfile = await this.getUserProfile();
          callback(userProfile);
        } catch (error) {
          console.error('Error getting user profile:', error);
          callback(null);
        }
      } else {
        callback(null);
      }
    });
  }

  // Get current user
  getCurrentUser(): User | null {
    const firebaseUser = this.auth.currentUser;
    return firebaseUser ? this.mapFirebaseUser(firebaseUser) : null;
  }

  // Refresh user profile from Firestore
  async refreshUserProfile(): Promise<User | null> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) {
      return null;
    }

    try {
      const userProfile = await this.getUserProfile();
      return userProfile;
    } catch (error) {
      console.error('Error refreshing user profile:', error);
      return null;
    }
  }

  // Get Firebase app instance
  getApp(): FirebaseApp {
    return this.app;
  }
}
